import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../components/Header";
import { userManagementUrl } from "../../config";
import { graphqlQuery } from "./DynamicService";
import debounce from "lodash/debounce"; // Import lodash debounce
import { useMediaQuery } from "@mui/system";

const DynamicForm = ({
  fields,
  onSubmit,
  title,
  subtitle,
  initialValues,
  onFieldChange,
}) => {
  const [dependentOptions, setDependentOptions] = useState({});
  const [userNames, setUserNames] = useState([]);
  const [isUsernameLoading, setIsUsernameLoading] = useState(false);
  const [phone, setPhone] = useState([]);
  const [email, setEmail] = useState([]);
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const isMediumScreen = useMediaQuery("(max-width:900px)");

  // Fetching the usernames for validation
  useEffect(() => {
    async function fetchValidationData() {
      const allUsername = `
      {
        allUserNames{
        email
        phone
        username
        }
      }`;
      try {
        const response = await graphqlQuery(allUsername, userManagementUrl.uri);
        if (response && response.length > 0) {
          const allUsernames = response.map((user) => user.username);
          setUserNames(allUsernames);
          const allPhoneNumber = response.map(
            (phoneNumber) => phoneNumber.phone
          );
          setPhone(allPhoneNumber);
          const allEmail = response.map((email) => email.email);
          setEmail(allEmail);
        }
      } catch (error) {
      }
    }
    fetchValidationData();
  }, []);
  // Debounced function to check for username existence
  const checkUsernameExists = useCallback(
    debounce(async (value) => {
      setIsUsernameLoading(true);
      const usernameExists = userNames.includes(value);
      setIsUsernameLoading(false);
      return usernameExists; // Return true or false
    }, 500),
    [userNames]
  );
  const checkPhoneNUmber = useCallback(
    debounce(async (value) => {
      const phoneNumberExists = phone.includes(value);

      return phoneNumberExists;
    }, 500),
    [userNames]
  );
  const checkEmailAddress = useCallback(
    debounce(async (value) => {
      const emailAddressExists = email.includes(value);
      return emailAddressExists;
    }, 500),
    [userNames]
  );

  // Define the validation schema with real-time username validation
  const validationSchema = yup.object().shape(
    fields.reduce((acc, field) => {
      let schema;

      switch (field.name) {
        case "email":
          schema = yup
            .string()
            .email("Invalid email format")
            .required(`${field.label} is required`);
          // .test(
          //   "checkUniqueUsername",
          //   "Username already exists",
          //   async function (value) {
          //     const { createError } = this;

          //     if (!value) return true;

          //     const EmailAddressExists = await checkEmailAddress(value);

          //     if (EmailAddressExists) {
          //       return createError({ message: "Email already exists" });
          //     }
          //     return true;
          //   }
          // )
          break;
        case "phone":
          schema = yup
            .string()
            .matches(/^\d+$/, "Phone number must be digits only")
            .min(10, "Phone number must be exactly 10 digits")
            .max(10, "Phone number must not exceed 10 digits")
            .required(`${field.label} is required`);
          // .test(
          //   "checkUniqueUsername",
          //   "Username already exists",
          //   async function (value) {
          //     const { createError } = this;

          //     if (!value) return true;

          //     const phoneNumberExists = await checkPhoneNUmber(value);

          //     if (phoneNumberExists) {
          //       return createError({ message: "PhoneNUmber already exists" });
          //     }
          //     return true;
          //   }
          // )

          break;
        case "amountpaid":
          schema = yup
            .number()
            .required.typeError("Amount must be a number")
            .required("Amount is required")
            .positive("Amount must be greater than 0")
            .max(
              yup.ref("outstandingBalance"),
              "Amount paid cannot exceed outstanding balance"
            );

          break;
        case "username":
          schema = yup.string().required("Username is required");
          // .test(
          //   "checkUniqueUsername",
          //   "Username already exists",
          //   async function (value) {
          //     const { createError } = this;

          //     if (!value) return true;

          //     const usernameExists = await checkUsernameExists(value);

          //     if (usernameExists) {
          //       return createError({ message: "Username already exists" });
          //     }
          //     return true;
          //   }
          // )
          break;

        case "text":
          schema = yup.string().nullable();
          break;
        case "number":
          schema = yup.number().nullable();
          break;
        case "date":
          schema = yup.date().nullable();
          break;
        case "select":
          schema = yup.string();
          break;
        case "multiselect":
          schema = yup.array().nullable();
          break;
        default:
          schema = yup.string().nullable();
      }

      if (field.required) {
        schema = schema.required(`${field.label} is required`);
      }

      acc[field.name] = schema;
      return acc;
    }, {})
  );

  const handleFieldChangeInternal = async (
    setFieldValue,
    setFieldError,
    field,
    value,
    values
  ) => {
    setFieldValue(field.name, value);

    if (field.name === "username" && value) {
      // Trigger username validation when the user types
      checkUsernameExists(value, setFieldError);
    }
    if (field.name === "phone" && value) {
      checkPhoneNUmber(value, setFieldError);
    }

    if (field.parent === "main") {
      const options = await onFieldChange(field, value);
      setDependentOptions(options || []);
    }
  };

  const shouldDisableField = (field, values) => {
    // General condition to disable fields based on any condition
    if (field.disableCondition) {
      const disableField = fields.find(
        (f) => f.name === field.disableCondition.fieldName
      );
      return values[disableField.name] === field.disableCondition.value;
    }
    return false;
  };

  return (
    <Box m="20px">
      <Header title={title} subtitle={subtitle} />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldError,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              sx={{
                gridTemplateColumns: {
                  xs: "1fr", // phones
                  sm: "repeat(2, 1fr)", // tablets
                  md: "repeat(3, 1fr)", // small desktops
                  lg: "repeat(4, 1fr)", // large desktops
                },
              }}
              // gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              // gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))"
            >
              {fields.map((field) => {
                const isDependent =
                  field.dependentOn && !values[field.dependentOn];
                const isDisabled = shouldDisableField(field, values);
                return (
                  <Box
                    key={field.name}
                    sx={{ gridColumn: field.gridColumn || "span 1" }}
                  >
                    {field.type === "select" || field.type === "multiselect" ? (
                      <Autocomplete
                        disabled={isDependent}
                        multiple={field.type === "multiselect"}
                        options={
                          field.parent === "dependent"
                            ? dependentOptions || []
                            : field.options || []
                        }
                        getOptionLabel={(option) =>
                          option ? option.label : ""
                        }
                        onChange={(event, newValue) => {
                          handleFieldChangeInternal(
                            setFieldValue,
                            setFieldError,
                            field,
                            field.type === "multiselect"
                              ? newValue.map((item) => item.value)
                              : newValue
                              ? newValue.value
                              : "",
                            values
                          );
                        }}
                        onBlur={handleBlur}
                        renderInput={(params) => {
                          if (field.name === "wholesalePrice") {
                            return (
                              <TextField
                                {...params}
                                label={field.label}
                                variant="filled"
                                name={field.name}
                                error={
                                  !!touched[field.name] && !!errors[field.name]
                                }
                                helperText={
                                  touched[field.name] && errors[field.name]
                                }
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: <span>%</span>, // Add a percentage symbol
                                }}
                                onInput={(e) => {
                                  const value = e.target.value;
                                  if (!/^\d+(\.\d+)?$/.test(value)) {
                                    e.target.value = value.replace(
                                      /[^0-9.]/g,
                                      ""
                                    ); // Allow only positive numbers and decimal
                                  }
                                }}
                              />
                            );
                          } else {
                            return (
                              <TextField
                                {...params}
                                label={field.label}
                                variant="filled"
                                name={field.name}
                                error={
                                  !!touched[field.name] && !!errors[field.name]
                                }
                                helperText={
                                  touched[field.name] && errors[field.name]
                                }
                              />
                            );
                          }
                        }}
                        value={
                          Array.isArray(field.options)
                            ? field.type === "multiselect"
                              ? field.options.filter(
                                  (option) =>
                                    Array.isArray(values[field.name]) &&
                                    values[field.name]?.includes(option.value)
                                )
                              : field.options.find(
                                  (option) =>
                                    option.value === values[field.name]
                                ) || null
                            : null
                        }
                      />
                    ) : field.type === "checkbox" ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values[field.name]}
                            onChange={(event) => {
                              handleFieldChangeInternal(
                                setFieldValue,
                                setFieldError,
                                field,
                                event.target.checked,
                                values
                              );
                            }}
                            name={field.name}
                          />
                        }
                        label={field.label}
                      />
                    ) : field.type === "disabled" ? (
                      <TextField
                        disabled={isDisabled}
                        fullWidth
                        variant="filled"
                        type={field.type}
                        label={field.label}
                        onBlur={handleBlur}
                        onChange={(event) => {
                          handleFieldChangeInternal(
                            setFieldValue,
                            setFieldError,
                            field,
                            event.target.value,
                            values
                          );
                        }}
                        value={values[field.name]}
                        name={field.name}
                        error={!!touched[field.name] && !!errors[field.name]}
                        helperText={touched[field.name] && errors[field.name]}
                        InputProps={{
                          endAdornment: field.name === "username" &&
                            isUsernameLoading && <CircularProgress size={20} />,
                        }}
                        inputProps={{ readOnly: true }}
                      />
                    ) : (
                      <TextField
                        disabled={isDisabled}
                        fullWidth
                        variant="filled"
                        type={field.type}
                        label={field.label}
                        onBlur={handleBlur}
                        onChange={(event) => {
                          handleFieldChangeInternal(
                            setFieldValue,
                            setFieldError,
                            field,
                            event.target.value,
                            values
                          );
                        }}
                        value={values[field.name]}
                        name={field.name}
                        error={!!touched[field.name] && !!errors[field.name]}
                        helperText={touched[field.name] && errors[field.name]}
                        InputProps={{
                          endAdornment: field.name === "username" &&
                            isUsernameLoading && <CircularProgress size={20} />,
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                Submit
              </Button>
            </Box>
            {isSubmitting && <CircularProgress />}
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default DynamicForm;
